import { InputWithSuffix } from '@components/ui/input-group'
import { Slider } from '@components/ui/slider'
import { ToggleGroup, ToggleGroupItem } from '@components/ui/toggle-group'
import { cn } from '@/utils'
import { FONT_SIZE_MAX, FONT_SIZE_MIN, FONT_SIZE_PRESETS } from '../constants/typography-ranges'
import { keepTextEditFocus } from '../handlers/keep-text-edit-focus'
import type { FontSizeField } from '../hooks/use-font-size-field'
import { FieldError } from './field-error'
import { SectionHeading } from './section-heading'

interface FontSizeSectionProps {
  field: FontSizeField
  restoreActiveEditor: () => void
}

/** Size: font size preset chips, slider and numeric input. */
export function FontSizeSection({ field, restoreActiveEditor }: FontSizeSectionProps) {
  const disabled = field.fontSizeControlsDisabled
  // Filled variant, like the X/Y/W/H inputs.
  const fontSizeFilled = field.fontSizeInputValue !== ''

  return (
    <div className="space-y-3" onPointerDown={keepTextEditFocus}>
      <SectionHeading>Size</SectionHeading>
      <label className="text-xs font-medium text-foreground leading-none block mt-2">
        Font Size
      </label>

      {/* Font Size Presets */}
      <ToggleGroup
        type="single"
        value={field.activeFontSizePreset}
        onValueChange={field.handlePresetChange}
        disabled={disabled}
        className="justify-start gap-1 mt-1"
        variant="outline"
        size="sm"
      >
        {FONT_SIZE_PRESETS.map((size) => (
          <ToggleGroupItem
            key={size}
            value={size.toString()}
            className="h-7 px-2 min-w-[2.5rem] text-xs font-medium leading-normal"
          >
            {size}pt
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {/* Font Size Slider */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-[1.125rem] mt-2">
          <Slider
            value={field.fontSizeMixed ? [] : field.fontSize}
            onValueChange={field.handleSliderChange}
            onValueCommit={restoreActiveEditor}
            disabled={disabled}
            min={FONT_SIZE_MIN}
            max={FONT_SIZE_MAX}
            step={1}
            className="flex-1 min-w-0 max-w-[8.125rem]"
          />
          <div className="flex flex-col" data-text-edit-keepalive="">
            <InputWithSuffix
              type="text"
              inputMode="numeric"
              value={field.fontSizeInputValue}
              disabled={disabled}
              onChange={field.handleInputChange}
              onFocus={field.handleInputFocus}
              onKeyDown={field.handleInputKeyDown}
              onBlur={field.handleInputBlur}
              size="compact"
              placeholder={field.fontSizeMixed ? 'Mixed' : 'Value'}
              // A value uses the foreground token instead of the compact input's muted default.
              data-filled={fontSizeFilled ? true : undefined}
              className={cn(fontSizeFilled && 'text-foreground')}
              error={field.fontSizeError}
              aria-describedby={field.fontSizeError ? 'font-size-error' : undefined}
              suffix={<span className="text-xs text-muted-foreground pl-1">pt</span>}
            />
            {field.fontSizeError && (
              <FieldError id="font-size-error">
                Values: {FONT_SIZE_MIN} - {FONT_SIZE_MAX}
              </FieldError>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
